

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  User,
  AlertCircle,
  Clock,
  Tag,
  Calendar,
  Flag,
  Search,
  X,
  Plus,
  Check,
  ChevronDown,
  GitBranch,
  GitCommit,
  Copy,
  Loader2,
  RefreshCw,
} from "lucide-react";
import AvatarTooltip from "@/components/AvatarTooltip";
import { Task, Sprint, TaskWorklog } from "@/types";
import { sprintAPI } from "@/api/sprintAPI";
import toast from "react-hot-toast";
// Loader not used here; kept only in ActivitySection
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateTask } from "@/store/taskSlice";
import { subtaskAPI } from "@/api/subtaskAPI";
import { taskAPI } from "@/api/taskAPI";
import type { Subtask } from "@/types";
import EditableEstimateField from "./EditableEstimateField";
import { LogWorkModal } from "./LogWorkModal";
import { formatMinutes } from "@/utils/timeTracking";
import { applyTaskUpdate } from "@/store/taskSlice";
import SidebarDetailItem from "./SidebarDetailItem";
import { mastersAPI, Priority, TaskType } from "@/api/mastersApi";
import { PriorityIcon } from "@/components/PriorityIcon";

const resolveTaskTypeIconUrl = (raw?: string | null) => {
  if (!raw) return null;
  const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
  if (builtInIcons.includes(raw.toLowerCase())) return null;
  try {
    if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    const api = new URL(API_BASE_URL);
    const origin = `${api.protocol}//${api.host}`;
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return origin + path;
  } catch {
    return null;
  }
};
import { TaskTypeIcon } from "@/components/TaskTypeIcon";
import { API_BASE_URL } from "@/utils/constants";

interface TaskDetails {
  assignee?: string;
  assigneeId?: string;
  labels?: string[];
  dueDate?: string;
  parent?: string;
  team?: string;
  testTeam?: string;
  sme?: string[];
  sprint?: string;
  originalEstimate?: string;
  timeRemaining?: string;
  development?: string;
  reporter?: string;
  organization?: string;
}

interface DetailsSidebarProps {
  taskDetails: TaskDetails | any;
  task: Task;
  projectMembers: any[];
  onUpdateAssignee: (taskId: string, assigneeId: string | null) => Promise<void>;
  onUpdateFields?: (taskId: string, updates: any) => Promise<void>;
  createTrigger?: number;
  hideSubtasks?: boolean;
  projectKey?: string;
  isViewerOnly?: boolean;
}

export const DetailsSidebar: React.FC<DetailsSidebarProps> = ({
  taskDetails,
  task,
  projectMembers,
  onUpdateAssignee,
  onUpdateFields,
  createTrigger,
  hideSubtasks,
  projectKey,
  isViewerOnly
}) => {
  const dispatch = useAppDispatch();
  const { user: currentUser } = useAppSelector((state) => state.auth);

  // UI state
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedSprintId, setSelectedSprintId] = useState<string>(task?.sprint_id || "");
  const [sprintOptions, setSprintOptions] = useState<Sprint[]>([]);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Label state
  const [isLabelEditing, setIsLabelEditing] = useState(false);
  const [labelInput, setLabelInput] = useState("");
  const [labelLoading, setLabelLoading] = useState(false);

  // Due date state
  const [isDueDateEditing, setIsDueDateEditing] = useState(false);
  const [dueDateValue, setDueDateValue] = useState<string>(task?.due_date || "");

  // Subtasks state
  const [subtasks, setSubtasks] = useState<Subtask[] | null>(null);
  const [subtaskInput, setSubtaskInput] = useState("");
  const [isSubtasksOpen, setIsSubtasksOpen] = useState(false);
  const createInputRef = useRef<HTMLInputElement | null>(null);

  // Time tracking state
  const [worklogs, setWorklogs] = useState<TaskWorklog[]>([]);
  const [isWorklogsLoading, setIsWorklogsLoading] = useState(false);
  const [isLogWorkOpen, setIsLogWorkOpen] = useState(false);
  const [isLoggingWork, setIsLoggingWork] = useState(false);
  const [localTimeSpent, setLocalTimeSpent] = useState<number>(task?.time_spent_minutes || 0);
  const [localRemaining, setLocalRemaining] = useState<number | undefined>(
    typeof task?.remaining_time_minutes === "number" ? task.remaining_time_minutes : undefined
  );

  // Git development state
  const [showDevelopmentDropdown, setShowDevelopmentDropdown] = useState(true);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [showCommitDropdown, setShowCommitDropdown] = useState(false);
  const developmentRef = useRef<HTMLDivElement | null>(null);

  // Priority state
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [selectedPriorityId, setSelectedPriorityId] = useState<string | undefined>(String((task as any)?.priority_id || ''));
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const priorityRef = useRef<HTMLDivElement | null>(null);
  const [isPriorityUpdating, setIsPriorityUpdating] = useState(false);
  const [showWorklogs, setShowWorklogs] = useState(false);
  const [showSprintDropdown, setShowSprintDropdown] = useState(false);
  const sprintRef = useRef<HTMLDivElement | null>(null);


  // Normalize labels: prefer task.labels (server source), fallback to taskDetails
  const labels: string[] = Array.isArray(task?.labels)
    ? task.labels
    : Array.isArray(taskDetails?.labels)
      ? taskDetails.labels
      : [];

  // Destructure display values (fallbacks from taskDetails)
  // Only extract values that are actually used in this component
  const sprint = (taskDetails && taskDetails.sprint) || "Not set";

  // Keep local selectedSprintId/dueDateValue in sync with task prop changes
  useEffect(() => {
    setSelectedSprintId(task?.sprint_id || "");
  }, [task?.sprint_id]);

  useEffect(() => {
    setDueDateValue(task?.due_date || "");
  }, [task?.due_date]);

  useEffect(() => {
    setLocalTimeSpent(task?.time_spent_minutes || 0);
    setLocalRemaining(
      typeof task?.remaining_time_minutes === "number" ? task.remaining_time_minutes : undefined
    );
  }, [task?.time_spent_minutes, task?.remaining_time_minutes, task?.id]);

  const loadWorklogs = useCallback(async () => {
    if (!task?.id) return;
    setIsWorklogsLoading(true);
    try {
      const logs = await taskAPI.getTaskWorklogs(task.id);
      setWorklogs(logs);
    } catch (error) {
      console.error("Failed to load worklogs", error);
    } finally {
      setIsWorklogsLoading(false);
    }
  }, [task?.id]);

  useEffect(() => {
    loadWorklogs();
  }, [loadWorklogs]);

  // Click outside to close assignee dropdown
  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(ev.target as Node)) {
        setShowAssigneeDropdown(false);
      }
    };

    if (showAssigneeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAssigneeDropdown]);

  // Load sprints for project
  useEffect(() => {
    const load = async () => {
      if (!task?.project_id) return;
      try {
        const sprints = await sprintAPI.getSprints(task.project_id);
        setSprintOptions(sprints);
      } catch (e) {
        // ignore non-critical
      }
    };
    load();
  }, [task?.project_id]);

  // Load priorities
  useEffect(() => {
    (async () => {
      try {
        const prios = await mastersAPI.getPriorities();
        setPriorities(prios || []);
      } catch {
        setPriorities([]);
      }
    })();
  }, []);

  // Sync selectedPriorityId with task changes
  useEffect(() => {
    setSelectedPriorityId(String((task as any)?.priority_id || ''));
  }, [(task as any)?.priority_id]);

  // Load subtasks lazily when section is expanded first time
  useEffect(() => {
    const load = async () => {
      if (!isSubtasksOpen || subtasks !== null) return;
      try {
        const items = await subtaskAPI.list(task.id);
        setSubtasks(items);
      } catch { }
    };
    load();
  }, [isSubtasksOpen, subtasks, task.id]);

  // External trigger from header button: open section and focus input
  useEffect(() => {
    if (typeof createTrigger === 'number') {
      setIsSubtasksOpen(true);
      setTimeout(() => createInputRef.current?.focus(), 0);
    }
  }, [createTrigger]);

  // Helper: color seed for assignee avatars
  const colorFromSeed = (seed: string) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-red-500",
      "bg-yellow-500",
      "bg-teal-500",
    ];
    if (!seed) return "bg-gray-500";
    const sum = Array.from(seed).reduce((a, c) => a + c.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };

  const getAssigneeInfo = () => {
    if (!task?.assigned_to) return { name: "Unassigned", initials: "", color: "bg-gray-500" };

    // Convert both to strings for comparison to handle type mismatches
    const assignedToStr = String(task.assigned_to);
    const member = projectMembers.find((m) => String(m.user_id) === assignedToStr);

    if (member) {
      const name = member.user_display_name || member.username || member.user_email || "User";
      const initials = name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
      const color = colorFromSeed(member.user_id || member.user_email || name);
      return { name, initials, color };
    }

    // Fallback: try to use assigned_user_name from task if available (from API)
    const assignedUserName = (task as any)?.assigned_user_name;
    if (assignedUserName) {
      const initials = assignedUserName
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
      const color = colorFromSeed(assignedToStr || assignedUserName);
      return { name: assignedUserName, initials, color };
    }

    return { name: "Unknown User", initials: "UU", color: "bg-gray-500" };
  };

  const getReporterInfo = () => {
    if (!task?.reporter_id) return { name: "Unassigned", initials: "", color: "bg-gray-500" };

    // Convert both to strings for comparison to handle type mismatches
    const reporterIdStr = String(task.reporter_id);
    const member = projectMembers.find((m) => String(m.user_id) === reporterIdStr);

    if (member) {
      const name = member.user_display_name || member.username || member.user_email || "User";
      const initials = name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
      const color = colorFromSeed(member.user_id || member.user_email || name);
      return { name, initials, color };
    }

    // Fallback: try to use reporter_name from task if available (from API)
    const reporterName = (task as any)?.reporter_name;
    if (reporterName) {
      const initials = reporterName
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
      const color = colorFromSeed(reporterIdStr || reporterName);
      return { name: reporterName, initials, color };
    }

    return { name: "Unknown User", initials: "UU", color: "bg-gray-500" };
  };

  const assigneeInfo = getAssigneeInfo();
  const reporterInfo = getReporterInfo();

  // Assignee dropdown list (searchable)
  const assignees = [
    { id: null as null | string, name: "Unassigned", initials: "", email: "", color: "bg-gray-500" },
    ...projectMembers.map((m) => ({
      id: m.user_id,
      name: m.user_display_name || m.username || m.user_email || "User",
      initials: ((m.user_display_name || m.username || m.user_email || "U")
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2)),
      email: m.user_email || "",
      color: colorFromSeed(m.user_id || m.user_email || ""),
    })),
  ];

  const filteredAssignees = assignees.filter(
    (a) => a.name.toLowerCase().includes(assigneeSearch.toLowerCase()) || a.email.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const currentUserId = currentUser?.id ? String(currentUser.id) : null;
  const currentUserName =
    currentUser?.name ||
    currentUser?.display_name ||
    [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ").trim() ||
    currentUser?.username ||
    currentUser?.email ||
    "You";
  const currentUserInitials = currentUserName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
  const currentUserColor = colorFromSeed(currentUserId || currentUser?.email || currentUserName);
  const isAssignedToCurrentUser = currentUserId ? String(task.assigned_to ?? "") === currentUserId : false;

  // Git helper functions
  const taskId = `${projectKey || 'AHM'}-${(task as any).display_number ?? task.id?.substring(0, 6)}`;

  const generateBranchName = () => {
    const cleanTitle = task.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return `${taskId}-${cleanTitle}`;
  };

  const handlePriorityChange = async (priorityId?: string | null) => {
    const nextId = priorityId ?? undefined;
    if (String(nextId || '') === String(selectedPriorityId || '')) {
      setShowPriorityDropdown(false);
      return;
    }
    setIsPriorityUpdating(true);
    try {
      setSelectedPriorityId(nextId ? String(nextId) : undefined);
      await dispatch(updateTask({
        id: task.id,
        data: { priority_id: nextId ? String(nextId) : undefined }
      })).unwrap();

      if (onUpdateFields) {
        await onUpdateFields(task.id, { priority_id: nextId ? String(nextId) : undefined });
      }

      toast.success(nextId ? "Priority updated" : "Priority removed");
      setShowPriorityDropdown(false);
    } catch (error: any) {
      console.error("Failed to update priority", error);
      toast.error(error?.message || "Failed to update priority");
      setSelectedPriorityId(String((task as any)?.priority_id || ''));
    } finally {
      setIsPriorityUpdating(false);
    }
  };

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(successMessage);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (developmentRef.current && !developmentRef.current.contains(event.target as Node)) {
        setShowBranchDropdown(false);
        setShowCommitDropdown(false);
      }
      if (priorityRef.current && !priorityRef.current.contains(event.target as Node)) {
        setShowPriorityDropdown(false);
      }
      if (sprintRef.current && !sprintRef.current.contains(event.target as Node)) {
        setShowSprintDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handlers
  const handleAssigneeChange = async (newAssigneeId: string | null) => {
    // Convert both to strings for comparison to handle type mismatches
    if (String(newAssigneeId) === String(task.assigned_to)) {
      setShowAssigneeDropdown(false);
      return;
    }
    setIsUpdating(true);
    try {
      await onUpdateAssignee(task.id, newAssigneeId);
      setShowAssigneeDropdown(false);
      setAssigneeSearch("");
    } catch (e) {
      console.error("Failed to update assignee:", e);
      toast.error("Failed to update assignee");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddLabel = async () => {
    const newLabel = labelInput.trim();
    if (!newLabel) return;

    const updated = [...labels, newLabel];

    setLabelLoading(true);
    try {
      await dispatch(updateTask({
        id: task.id,
        data: {
          labels: updated
        }
      })).unwrap();

      // Notify parent to refresh task
      if (onUpdateFields) {
        await onUpdateFields(task.id, { labels: updated });
      }

      setLabelInput("");
      setIsLabelEditing(false);
      toast.success("Label added");
    } catch (err: any) {
      toast.error("Failed to add label");
    } finally {
      setLabelLoading(false);
    }
  };


  const handleRemoveLabel = async (label: string) => {
    const updated = labels.filter((l) => l !== label);

    setLabelLoading(true);
    try {
      await dispatch(updateTask({
        id: task.id,
        data: {
          labels: updated
        }
      })).unwrap();

      // Notify parent to refresh task
      if (onUpdateFields) {
        await onUpdateFields(task.id, { labels: updated });
      }

      toast.success("Label removed");
    } catch (err: any) {
      console.error("REMOVE FAILED", err);
      toast.error("Failed to remove label");
    } finally {
      setLabelLoading(false);
    }
  };

  const handleDueDateChange = async (date: string) => {
    try {
      if (!date || !date.trim()) {
        toast.error("Due date cannot be empty");
        return;
      }
      // Convert date string (YYYY-MM-DD) to ISO datetime format (YYYY-MM-DDTHH:mm:ss)
      // API expects datetime format with T separator, not just date
      let dateValue: string | null = null;
      if (date && date.trim()) {
        // If date is just YYYY-MM-DD, append T00:00:00 to make it a valid datetime
        // Format: YYYY-MM-DDTHH:mm:ss (without timezone to avoid shifting dates)
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          // Direct format without timezone conversion to preserve the exact date
          dateValue = `${date}T00:00:00`;
        } else if (date.includes('T') || date.includes(' ')) {
          // Already in datetime format, ensure T separator is used
          dateValue = date.includes('T') ? date : date.replace(' ', 'T');
        } else {
          // Fallback: use as is
          dateValue = date;
        }
      }

      // Ensure dateValue is undefined, not null, to satisfy type requirements
      await dispatch(updateTask({ id: task.id, data: { due_date: dateValue ?? undefined } })).unwrap();

      // Notify parent to refresh task
      if (onUpdateFields) {
        await onUpdateFields(task.id, { due_date: dateValue ?? undefined });
      }

      // keep local state in sync with the date part (YYYY-MM-DD) for display
      setDueDateValue(dateValue ? dateValue.split('T')[0] : date);
      setIsDueDateEditing(false);
      toast.success("Due date updated");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to update due date");
    }
  };

  const formatDateDDMMYYYY = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr; // fallback
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTimeShort = (value?: string) => {
    if (!value) return "-";

    // Parse the date - API returns UTC ISO strings
    // If date doesn't have timezone info, assume it's UTC
    let date: Date;

    if (typeof value === 'string') {
      const tzRegex = /([zZ]|[+-]\d{2}:\d{2})$/;
      if (tzRegex.test(value)) {
        date = new Date(value);
      } else if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        date = new Date(value + 'Z');
      } else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(value + 'T00:00:00Z');
      } else {
        date = new Date(value);
      }
    } else {
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) {
      console.warn('Invalid date value:', value);
      return value;
    }

    // Format using local timezone - toLocaleString automatically converts from UTC to local time
    // This ensures UTC times from the server are displayed in the user's local timezone
    return date.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: undefined, // Use browser's local timezone
    });
  };

  const timeTrackingSummary = useMemo(() => {
    const original = task?.original_estimate_minutes ?? 0;
    const spent = localTimeSpent;
    const remaining = localRemaining ?? task?.remaining_time_minutes ?? 0;
    const burned = original > 0 ? Math.min(100, Math.round((spent / original) * 100)) : undefined;
    return {
      original,
      spent,
      remaining,
      burned,
    };
  }, [task?.original_estimate_minutes, task?.remaining_time_minutes, localTimeSpent, localRemaining]);

  const handleLogWorkSubmit = useCallback(async (payload: {
    time_spent_minutes: number;
    started_at?: string;
    comment?: string;
    adjust_remaining: "auto" | "set" | "none";
    remaining_time_minutes?: number | null;
  }) => {
    if (!task?.id) return;
    setIsLoggingWork(true);
    try {
      const response = await taskAPI.addWorklog(task.id, payload);
      setWorklogs((prev) => [
        response.worklog,
        ...prev.filter((log) => log.id !== response.worklog.id),
      ]);
      setLocalTimeSpent(response.task.time_spent_minutes || 0);
      setLocalRemaining(
        typeof response.task.remaining_time_minutes === "number"
          ? response.task.remaining_time_minutes
          : undefined
      );
      dispatch(applyTaskUpdate(response.task));
      toast.success("Work logged");
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message;
      toast.error(detail || "Failed to log work");
      throw error;
    } finally {
      setIsLoggingWork(false);
    }
  }, [dispatch, task?.id]);


  const onUpdateSprint = async (sprintId: string) => {
    try {
      await dispatch(updateTask({ id: task.id, data: { sprint_id: sprintId || undefined } })).unwrap();
      setSelectedSprintId(sprintId);

      // Notify parent to refresh task
      if (onUpdateFields) {
        await onUpdateFields(task.id, { sprint_id: sprintId || undefined });
      }

      toast.success(sprintId ? "Sprint updated" : "Moved to backlog");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to update sprint");
    }
  };

  return (
    <div className=""> 
      <div className="space-y-5 border rounded-md p-3">
        <h3 className="text-lg font-semibold text-gray-900">Details</h3>

        {/* Assignee */}
        <div className="" ref={dropdownRef}>
          <SidebarDetailItem icon={User} label="Assignee" value={assigneeInfo.name}>
            <div className="relative flex gap-2 items-center flex-1">
              <button
                onClick={() => setShowAssigneeDropdown((s) => !s)}
                disabled={isUpdating}
                className="flex items-center gap-2 p-2 w-full text-left hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors disabled:opacity-50"
              >
                <AvatarTooltip label={assigneeInfo.name || 'Unassigned'}>
                  <div
                    className={`w-6 h-6 ${assigneeInfo.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                  >
                    {assigneeInfo.initials || <User className="w-3 h-3" />}
                  </div>
                </AvatarTooltip>
                <span className="text-sm text-gray-900 flex-1 truncate">{isUpdating ? "Updating..." : assigneeInfo.name}</span>
              </button>

              {showAssigneeDropdown && !isViewerOnly && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search assignees..."
                        value={assigneeSearch}
                        onChange={(e) => setAssigneeSearch(e.target.value)}
                        className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                    </div>
                  </div>
                  {currentUserId && (
                    <div className="border-b border-gray-100">
                      <button
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors ${isAssignedToCurrentUser ? "bg-blue-50" : ""}`}
                        disabled={isAssignedToCurrentUser || isUpdating}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (currentUserId) handleAssigneeChange(currentUserId);
                        }}
                      >
                        <AvatarTooltip label={currentUserName} position="bottom">
                          <div
                            className={`w-6 h-6 ${currentUserColor} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                          >
                            {currentUserInitials}
                          </div>
                        </AvatarTooltip>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">Assign to Me</div>
                          <div className="text-xs text-gray-500 truncate">{currentUserName}</div>
                        </div>
                        {isAssignedToCurrentUser && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    </div>
                  )}

                  <div className="max-h-48 overflow-y-auto taskmodal-scroll">
                    {filteredAssignees.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 text-center">No assignees found</div>
                    ) : (
                      filteredAssignees.map((a) => (
                        <button
                          key={a.id ?? "unassigned"}
                          disabled={isUpdating}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent focus changes
                            handleAssigneeChange(a.id ?? null);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 ${String(task.assigned_to) === String(a.id) ? "bg-blue-50 border-l-2 border-l-blue-500" : ""}`}
                        >
                          <AvatarTooltip label={a.name || 'Unassigned'} position="bottom">
                            <div
                              className={`w-6 h-6 ${a.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                            >
                              {a.initials || <User className="w-3 h-3" />}
                            </div>
                          </AvatarTooltip>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{a.name}</div>
                            {a.email && <div className="text-xs text-gray-500 truncate">{a.email}</div>}
                          </div>
                          {String(task.assigned_to) === String(a.id) && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </SidebarDetailItem>
        </div>

        {/* Labels */}
        <SidebarDetailItem icon={Tag} label="Labels" value={labels?.join(", ") || "None"}>
          <div className="flex flex-wrap gap-1 p-1">
            {labels.map((label, idx) => (
                <div key={idx} className="flex items-center px-3 py-1 gap-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <span>{label}</span>
                {!isViewerOnly && (
                <button
                  type="button"
                  className="flex items-center justify-between mt-0.5 text-gray-400 hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLabel(label);
                  }}
                  disabled={labelLoading}
                >
                  <X className="w-3 h-3" />
                </button>
               )}
              </div>
            ))}

            {labelLoading && (
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs text-blue-700 bg-blue-50 rounded-full">
                <Loader2 className="w-3 h-3 animate-spin" />
                Updating labels...
              </span>
            )}
            {!isViewerOnly && (
             isLabelEditing ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddLabel();
                }}
                className="flex items-center"
              >
                <input
                  type="text"
                  value={labelInput}
                  onChange={(e) => setLabelInput(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                  placeholder="Add label"
                  autoFocus
                />
                <button type="submit" className="ml-1 text-blue-500 hover:text-blue-700">
                  <Plus className="w-4 h-4" />
                </button>
              </form>
            ) : (            
              <button
                type="button"
                className="flex items-center justify-between px-3 py-1 gap-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 ml-1"
                onClick={() => setIsLabelEditing(true)}
              >
                <Plus className="w-3 h-3" /> 
                <span>Add</span>
              </button>
            )
            )}

            {labels.length === 0 && !isLabelEditing && <span className="text-sm text-gray-500 mt-0.5 ml-1">No labels</span>}
          </div>
        </SidebarDetailItem>

        {/* Priority */}
        <div className="" ref={priorityRef}>
          <SidebarDetailItem icon={Flag} label="Priority" value={priorities.find(p => String(p.id) === String(selectedPriorityId))?.name || "Not set"}>
            <div className="relative flex gap-2 items-center flex-1">
              <button
                type="button"
                onClick={() => setShowPriorityDropdown((v) => !v)}
                disabled={isPriorityUpdating}
                className="flex items-center gap-2 px-1 py-2 w-full text-left hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors disabled:opacity-50"
              >
                {selectedPriorityId ? (
                  <>
                    <PriorityIcon priority={priorities.find(p => String(p.id) === String(selectedPriorityId)) || null} size={18} />
                    <span className="text-sm text-gray-900 flex-1 truncate">
                      {priorities.find(p => String(p.id) === String(selectedPriorityId))?.name || 'Not set'}
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-gray-500 flex-1 truncate">Select priority…</span>
                )}
                {/* <ChevronDown className="w-4 h-4 text-gray-400" /> */}
              </button>

              {showPriorityDropdown && !isViewerOnly && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="max-h-48 overflow-y-auto taskmodal-scroll">
                    <button
                      type="button"
                      disabled={isPriorityUpdating}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handlePriorityChange(undefined);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 ${selectedPriorityId ? '' : 'bg-blue-50 border-l-2 border-l-blue-500'}`}
                    >
                      <span className="text-sm text-gray-900 flex-1">None</span>
                      {!selectedPriorityId && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                    </button>
                    {priorities.map((p) => {
                      const pid = String(p.id);
                      const isActive = String(selectedPriorityId || '') === pid;
                      return (
                        <button
                          key={pid}
                          type="button"
                          disabled={isPriorityUpdating}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handlePriorityChange(pid);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 ${isActive ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                        >
                          <PriorityIcon priority={p} size={18} />
                          <span className="text-sm text-gray-900 flex-1 truncate">{p.name}</span>
                          {isActive && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </SidebarDetailItem>
        </div>

        {/* Due Date */}
        <SidebarDetailItem
          icon={Calendar}
          label="Due date"
          value={
            dueDateValue ? formatDateDDMMYYYY(dueDateValue)
              : "Not set"
          }
        >
          <div
            className={(() => {
              if (!dueDateValue) return "flex items-center gap-2 px-1 py-2 hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors";
              const due = new Date(dueDateValue);
              const today = new Date();
              const diffDays = Math.floor(
                (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
              );

              if (diffDays < 0)
                return "flex items-center gap-2 px-2 py-2 mr-2 hover:bg-gray-50 rounded border border-red-500 hover:border-red-500 transition-colors";
              if (diffDays <= 1)
                return "flex items-center gap-2 px-2 py-2 mr-2 hover:bg-gray-50 rounded border border-orange-400 hover:border-orange-400 transition-colors";
              return "flex items-center gap-2 px-2 py-2 mr-2 hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors";
            })()}
          >
            {isDueDateEditing ? (
              <div className="flex items-center gap-2 w-full">
                <input
                  type="date"
                  value={dueDateValue ? dueDateValue.slice(0, 10) : ""}
                  onChange={(e) => setDueDateValue(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                />
                <button
                  type="button"
                  className="text-sm font-medium text-blue-500 hover:text-blue-700"
                  onClick={() => handleDueDateChange(dueDateValue)}
                >
                  <Check className="w-4 h-4 text-green-500" />
                </button>
                <button
                  type="button"
                  className="text-sm font-medium text-gray-400 hover:text-gray-700"
                  onClick={() => {
                    setIsDueDateEditing(false);
                    setDueDateValue(task?.due_date || "");
                  }}
                >
                  <X className="w-4 h-4 text-red-600"/>
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  {dueDateValue && (() => {
                    const due = new Date(dueDateValue);
                    const today = new Date();
                    const diffDays = Math.floor(
                      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
                    );

                    if (diffDays < 0)
                      return (
                        <div className="flex items-center text-red-600 font-medium">
                          <AlertCircle className="w-4 h-4 mr-1" /> Overdue
                        </div>
                      );
                    if (diffDays <= 1)
                      return (
                        <div className="flex items-center text-orange-500 font-medium">
                          <Clock className="w-4 h-4 mr-1" /> Due soon
                        </div>
                      );
                    return (
                      <div className="flex items-center text-gray-600 font-medium">
                        On track
                      </div>
                    );
                  })()}

                  <span className="text-sm text-gray-900">
                    {dueDateValue ? formatDateDDMMYYYY(dueDateValue) : "Not set"}
                  </span>
                </div>
                 {!isViewerOnly && (
                <button
                  type="button"
                  className="text-blue-500 hover:text-blue-700 flex items-center gap-1"
                  onClick={() => setIsDueDateEditing(true)}
                >
                  <Calendar className="w-4 h-4" />
                </button>
                 )}
              </div>
            )}
          </div>
        </SidebarDetailItem>

        {/* Reporter */}
        <div className="">
          <SidebarDetailItem icon={User} label="Reporter" value={reporterInfo.name}>
            <div className="relative">
              <div
                className="flex items-center gap-2 p-2 w-full text-left rounded border border-transparent"
              >
                <AvatarTooltip label={reporterInfo.name || ''}>
                  <div
                    className={`w-6 h-6 ${reporterInfo.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                  >
                    {reporterInfo.initials || <User className="w-3 h-3" />}
                  </div>
                </AvatarTooltip>
                <span className="text-sm text-gray-900 flex-1 truncate">{reporterInfo.name}</span>
              </div>
            </div>
          </SidebarDetailItem>
        </div>

        {/* Time Tracking Section */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Time Tracking</h4>
            {!isViewerOnly && (
            <button
              type="button"
              onClick={() => setIsLogWorkOpen(true)}
              className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-100 rounded"
            >
              <Plus className="w-4 h-4" />
              Log work
            </button>
            )}
          </div>

          {timeTrackingSummary.original > 0 && (
            (() => {
              const progress = timeTrackingSummary.burned ?? 0;
              return (
                <div className="mb-4 bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{formatMinutes(localTimeSpent)} logged</span>
                    <span>{formatMinutes(timeTrackingSummary.original)} original</span>
                  </div>
                  <div className="w-full h-2 bg-white rounded overflow-hidden border border-gray-200">
                    <div
                      className="h-full bg-blue-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatMinutes(timeTrackingSummary.remaining)} remaining · {progress}% logged
                  </div>
                </div>
              );
            })()
          )}

          <SidebarDetailItem icon={Clock} label="Original Estimate">
            <EditableEstimateField
              value={task.original_estimate_minutes || 0}
              label="Original Estimate"
              field="original_estimate_minutes"
              taskId={task.id}
              onUpdateFields={onUpdateFields}
              isviewerOnly={isViewerOnly}
            />
          </SidebarDetailItem>

          <SidebarDetailItem icon={Clock} label="Time Spent" value={formatMinutes(localTimeSpent)}>
            <div className="p-2">{formatMinutes(localTimeSpent)}</div>
          </SidebarDetailItem>

          <SidebarDetailItem icon={Clock} label="Time Remaining">
            <EditableEstimateField
              value={(localRemaining ?? task.remaining_time_minutes) || 0}
              label="Time Remaining"
              field="remaining_time_minutes"
              taskId={task.id}
              onUpdateFields={onUpdateFields}
              isviewerOnly={isViewerOnly}
            />
          </SidebarDetailItem>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-medium text-gray-900">Worklogs</h5>
              {!showWorklogs ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowWorklogs(true);
                    loadWorklogs();
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                  disabled={isWorklogsLoading}
                >
                  Show log
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={loadWorklogs}
                    className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    disabled={isWorklogsLoading}
                    title="Refresh worklogs"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowWorklogs(false)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Close worklogs"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            {showWorklogs && (
              <>
                {isWorklogsLoading ? (
                  <div className="text-sm text-gray-500">Loading worklogs…</div>
                ) : worklogs.length === 0 ? (
                  <div className="text-sm text-gray-500">No work logged yet.</div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1 taskmodal-scroll">
                    {worklogs.map((log) => (
                      <div key={log.id} className="border border-gray-200 rounded p-2 bg-white shadow-sm">
                        <div className="flex items-center justify-between text-sm font-medium text-gray-900">
                          <span>{formatMinutes(log.time_spent_minutes)}</span>
                          <span className="text-xs text-gray-500">{formatDateTimeShort(log.started_at)}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {log.user_display_name || "User"}
                          {typeof log.remaining_time_minutes_after === "number" && (
                            <span className="text-gray-400">
                              {' '}
                              · Remaining {formatMinutes(log.remaining_time_minutes_after)}
                            </span>
                          )}
                        </div>
                        {log.comment && (
                          <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{log.comment}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Development (inline expandable) */}
        <div className="pt-4 border-t border-gray-200" ref={developmentRef}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Development</h4>

            <button
              onClick={() => setShowDevelopmentDropdown(!showDevelopmentDropdown)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showDevelopmentDropdown ? "rotate-180" : ""
                  }`}
              />
            </button>
          </div>

          {/* INLINE Section (NOT dropdown popup) */}
          {showDevelopmentDropdown && (
            <div className="mt-2 space-y-2 relative">

              {/* CREATE BRANCH (inline trigger + popup dropdown) */}
              <div className="relative w-full">
                <button
                  onClick={() => {
                    setShowBranchDropdown(!showBranchDropdown);
                    setShowCommitDropdown(false);
                  }}
                  className="w-full flex items-center justify-between px-0 pr-3 py-2 text-blue-600 hover:bg-gray-100 rounded transition-colors hover:underline"
                >
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4" />
                    <span className="font-medium">Create branch</span>
                  </div>

                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${showBranchDropdown ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {/* BRANCH DROPDOWN (popup style like previous) */}
                {showBranchDropdown && (
                  <div className="absolute right-0 w-[260px] max-w-full overflow-hidden top-full mt-1 mr-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
                    <div className="text-xs font-semibold text-gray-700 mb-2">
                      GIT CREATE & CHECKOUT A NEW BRANCH
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white border border-gray-500 rounded p-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                        <code className="text-sm text-gray-700 font-mono">
                          git checkout -b {generateBranchName()}
                        </code>
                      </div>

                      <button
                        onClick={() => {
                          copyToClipboard(
                            `git checkout -b ${generateBranchName()}`,
                            "Git command copied to clipboard!"
                          );
                          setShowBranchDropdown(false);
                          setShowDevelopmentDropdown(false);
                        }}
                        className="p-2 bg-gray-200 rounded text-gray-600 hover:bg-gray-300 transition-colors"
                        title="Copy command"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* CREATE COMMIT (popup above like previous UI) */}
              <div className="relative w-full">
                <button
                  onClick={() => {
                    setShowCommitDropdown(!showCommitDropdown);
                    setShowBranchDropdown(false);
                  }}
                  className="w-full flex items-center justify-between px-0 pr-3 py-2 text-blue-600 hover:bg-gray-100 rounded transition-colors hover:underline mt-1"
                >
                  <div className="flex items-center gap-2">
                    <GitCommit className="w-4 h-4" />
                    <span className="font-medium">Create commit</span>
                  </div>

                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${showCommitDropdown ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {/* COMMIT DROPDOWN (shows ABOVE button like your previous UI) */}
                {showCommitDropdown && (
                  <div className="absolute right-0 w-[260px] max-w-full overflow-hidden top-full mb-1 mr-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
                    <div className="text-xs text-gray-700 mb-3">
                      Include keys in your commit messages to link them to your tracker items.
                    </div>

                    <div className="space-y-4">

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Copy key
                        </label>

                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-white border border-gray-500 rounded p-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                            <code className="text-sm text-gray-700 font-mono">
                              {taskId}
                            </code>
                          </div>

                          <button
                            onClick={() => {
                              copyToClipboard(taskId, "Task key copied to clipboard!");
                              setShowCommitDropdown(false);
                              setShowDevelopmentDropdown(false);
                            }}
                            className="p-2 bg-gray-200 rounded text-gray-600 hover:bg-gray-300 transition-colors"
                            title="Copy task key"
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Copy sample Git commit
                        </label>

                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-white border border-gray-500 rounded p-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                            <code className="text-sm text-gray-700 font-mono">
                              git commit -m "{taskId} &lt;message&gt;"
                            </code>
                          </div>

                          <button
                            onClick={() => {
                              copyToClipboard(
                                `git commit -m "${taskId} <message>"`,
                                "Git commit template copied to clipboard!"
                              );
                              setShowCommitDropdown(false);
                              setShowDevelopmentDropdown(false);
                            }}
                            className="p-2 bg-gray-200 rounded text-gray-600 hover:bg-gray-300 transition-colors"
                            title="Copy commit template"
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                )}
              </div>

            </div>
          )}

        </div>


        <div className="" ref={sprintRef}>
          <SidebarDetailItem icon={Flag} label="Sprint" value={sprint}>
            <div className="relative flex gap-2 items-center flex-1">
              <button
                type="button"
                onClick={() => setShowSprintDropdown((v) => !v)}
                className="flex items-center gap-2 px-1 py-2 w-full text-left hover:bg-gray-50 rounded border border-transparent hover:border-gray-300 transition-colors"
              >
                <span className="text-sm text-gray-900 flex-1 truncate">
                  {sprintOptions.find((sp) => String(sp.id) === String(selectedSprintId))?.name || sprint || "Not set"}
                </span>
              </button>

              {showSprintDropdown && !isViewerOnly && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="max-h-48 overflow-y-auto taskmodal-scroll">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onUpdateSprint("");
                        setShowSprintDropdown(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${!selectedSprintId ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                    >
                      <span className="text-sm text-gray-900 flex-1 truncate">Backlog</span>
                      {!selectedSprintId && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                    </button>
                    {sprintOptions.map((sp) => {
                      const isActive = String(selectedSprintId || '') === String(sp.id);
                      return (
                        <button
                          key={sp.id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onUpdateSprint(String(sp.id));
                            setShowSprintDropdown(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${isActive ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                        >
                          <span className="text-sm text-gray-900 flex-1 truncate">{sp.name}</span>
                          {isActive && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </SidebarDetailItem>
        </div>
      </div>

      {/* Subtasks (hidden when requested) */}
      {!hideSubtasks && (
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-900">Subtasks {subtasks ? `(${subtasks.length})` : ""}</h4>
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setIsSubtasksOpen((s) => !s)}
            >
              {isSubtasksOpen ? "Hide" : "Show"}
            </button>
          </div>
          {isSubtasksOpen && (
            <div className="space-y-2">
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const title = subtaskInput.trim();
                  if (!title) return;
                  try {
                    const created = await subtaskAPI.create(task.id, { title });
                    setSubtasks((prev) => (prev ? ([...(prev as Subtask[]), created]) : [created]));
                    setSubtaskInput("");
                  } catch { }
                }}
                className="flex gap-2"
              >
                <input
                  type="text"
                  value={subtaskInput}
                  onChange={(e) => setSubtaskInput(e.target.value)}
                  placeholder="Create subtask"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  ref={createInputRef}
                />
                <button className="px-2 py-1 text-sm bg-blue-600 text-white rounded" type="submit">Add</button>
              </form>
              <div className="space-y-1">
                {(subtasks || []).map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={!!s.is_completed}
                      onChange={async () => {
                        try {
                          const updated = await subtaskAPI.update(s.id, { is_completed: !s.is_completed });
                          setSubtasks((prev) => (prev ? prev.map((p) => (p.id === s.id ? updated : p)) : prev));
                        } catch { }
                      }}
                      className="accent-blue-600"
                    />
                    <span className="truncate flex-1">{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Automation */}
      <div className="pt-4 border-gray-200 text-xs text-gray-500 space-y-1">
        <div>Created {task?.created_at ? new Date(task.created_at).toLocaleDateString() : "-"}</div>
        <div>Updated {task?.updated_at ? new Date(task.updated_at).toLocaleDateString() : "-"}</div>
      </div>
      <LogWorkModal
        isOpen={isLogWorkOpen}
        onClose={() => setIsLogWorkOpen(false)}
        onSubmit={handleLogWorkSubmit}
        task={task}
        isSubmitting={isLoggingWork}
      />
    </div>
  );
};




export default DetailsSidebar;